import { useNavigate } from 'react-router-dom';
import { useEffect, useState, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet';
import {
  ArrowRight, CheckCircle2, Shield, Scale, FileCheck, User,
  Menu, X, Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Footer } from '@/components/Footer';
import { LazySection } from '@/components/LazySection';
import { trackEvent, trackHeroCTA } from '@/lib/analytics';
import { usePageAnalytics } from '@/hooks/usePageAnalytics';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import founderPhoto from '@/assets/founder-andre-araujo-2026.webp';

// HeroSection — NO framer-motion dependency, renders instantly for fast LCP
import { HeroSection } from '@/components/home/HeroSection';

// Lazy-load ALL below-the-fold sections to defer framer-motion from critical path
const TargetAudienceSection = lazy(() => import('@/components/home/TargetAudienceSection').then(m => ({ default: m.TargetAudienceSection })));
const BeforeAfterSection = lazy(() => import('@/components/home/BeforeAfterSection').then(m => ({ default: m.BeforeAfterSection })));
const MemberValuePreview = lazy(() => import('@/components/home/MemberValuePreview').then(m => ({ default: m.MemberValuePreview })));
const TypicalCaseSection = lazy(() => import('@/components/home/TypicalCaseSection').then(m => ({ default: m.TypicalCaseSection })));
const ProblemSection = lazy(() => import('@/components/home/ProblemSection').then(m => ({ default: m.ProblemSection })));
const SolutionSection = lazy(() => import('@/components/home/SolutionSection').then(m => ({ default: m.SolutionSection })));
const HowItWorksSection = lazy(() => import('@/components/home/HowItWorksSection').then(m => ({ default: m.HowItWorksSection })));
const FinalCTASection = lazy(() => import('@/components/home/FinalCTASection').then(m => ({ default: m.FinalCTASection })));
const SEOPillarLinks = lazy(() => import('@/components/home/SEOPillarLinks').then(m => ({ default: m.SEOPillarLinks })));
const TechnicalGuidesLinks = lazy(() => import('@/components/home/TechnicalGuidesLinks').then(m => ({ default: m.TechnicalGuidesLinks })));
const AuthorityAssetsSection = lazy(() => import('@/components/home/AuthorityAssetsSection').then(m => ({ default: m.AuthorityAssetsSection })));

// --- JSON-LD Schemas ---
const founderSchema = {
  '@context': 'https://schema.org',
  '@type': 'Person',
  name: 'André Araujo',
  jobTitle: 'Engenheiro Eletrotécnico',
  description: 'Fundador da ElevAdvisor. Experiência em auditoria técnica independente de elevadores e mobilidade vertical.',
  url: 'https://www.linkedin.com/in/araujoandrelp',
  sameAs: ['https://www.linkedin.com/in/araujoandrelp'],
  worksFor: { '@id': 'https://elevadvisor.com/#organization' },
  knowsAbout: ['Auditoria técnica de elevadores', 'Norma EN 81', 'Mobilidade vertical', 'Segurança em elevadores', 'Governação técnica independente'],
};

const organizationSchema = { '@id': 'https://elevadvisor.com/#organization' };

const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'ElevAdvisor',
  description: 'Plataforma técnica de apoio à decisão no setor de elevadores. Análise técnica independente, classificação de risco e pareceres consultivos.',
  url: 'https://elevadvisor.com',
  serviceType: 'Apoio Técnico à Decisão em Elevadores',
  provider: { '@id': 'https://elevadvisor.com/#organization' },
  areaServed: { '@type': 'Country', name: 'Portugal' },
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Elevadvisor',
  url: 'https://elevadvisor.com',
  description: 'Plataforma técnica de apoio à decisão no setor de elevadores. Análise independente e pareceres consultivos.',
  publisher: { '@id': 'https://elevadvisor.com/#organization' },
};

const whenToUse = [
  'Recebeu uma recomendação técnica ou orçamento elevado',
  'Existe pressão para decidir rapidamente',
  'Há risco financeiro ou jurídico',
  'Precisa de uma segunda opinião técnica independente',
];

export default function Index() {
  const navigate = useNavigate();
  const [showApplicabilityModal, setShowApplicabilityModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isInstallable, isInstalled, promptInstall } = usePWAInstall();

  // Check if user is already signed in via Clerk global (no dynamic import needed)
  useEffect(() => {
    const clerkInstance = (window as any).Clerk;
    if (clerkInstance?.user) {
      navigate('/dashboard');
    }
  }, [navigate]);

  usePageAnalytics({ pagePath: '/' });

  return (
    <>
      <Helmet>
        <title>ElevAdvisor — Consultoria Técnica de Elevadores</title>
        <meta name="description" content="Apoio técnico independente para decisões informadas em elevadores. Análise consultiva para síndicos, gestores e administradoras." />
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        <link rel="canonical" href="https://elevadvisor.com" />
        <meta property="og:title" content="ElevAdvisor - Consultoria Técnica Independente para Elevadores" />
        <meta property="og:description" content="Apoio técnico independente para decisões informadas em elevadores. Análise consultiva para síndicos, gestores e administradoras." />
        <meta property="og:url" content="https://elevadvisor.com" />
        <meta property="og:image" content="https://elevadvisor.com/og-image.png" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border safe-area-inset-top">
          <div className="container flex h-16 items-center justify-between w-full px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
                <span className="text-primary-foreground font-bold text-sm">EA</span>
              </div>
              <span className="font-semibold text-lg text-foreground">ElevAdvisor</span>
            </div>

            <div className="hidden md:flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/elevadores')}>Elevadores</Button>
              <Button variant="ghost" onClick={() => navigate('/como-funciona')}>Como Funciona</Button>
              <Button variant="ghost" onClick={() => navigate('/planos-consultoria')} className="text-primary font-semibold">Consultoria</Button>
              <Button variant="ghost" onClick={() => navigate('/analises-tecnicas-elevadores')}>Conteúdos Técnicos</Button>
              {!isInstalled && (
                <Button variant="outline" onClick={() => isInstallable ? promptInstall() : navigate('/install')} className="gap-2 min-h-[44px] touch-manipulation">
                  <Download className="w-4 h-4" />Instalar App
                </Button>
              )}
              <Button variant="ghost" onClick={() => navigate('/members/auth?tab=login')}>Entrar</Button>
              <Button onClick={() => navigate('/members/auth')}>Criar Conta Gratuita</Button>
            </div>

            <button
              className="md:hidden p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-foreground touch-manipulation active:bg-muted/50 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden bg-background border-t border-border">
              <nav className="container py-4 flex flex-col gap-2">
                <Button variant="ghost" className="justify-start w-full" onClick={() => { navigate('/elevadores'); setMobileMenuOpen(false); }}>Elevadores</Button>
                <Button variant="ghost" className="justify-start w-full" onClick={() => { navigate('/como-funciona'); setMobileMenuOpen(false); }}>Como Funciona</Button>
                <Button variant="ghost" className="justify-start w-full text-primary font-semibold" onClick={() => { navigate('/planos-consultoria'); setMobileMenuOpen(false); }}>Consultoria</Button>
                <Button variant="ghost" className="justify-start w-full" onClick={() => { navigate('/analises-tecnicas-elevadores'); setMobileMenuOpen(false); }}>Conteúdos Técnicos</Button>
                {!isInstalled && (
                  <Button variant="outline" className="justify-start w-full gap-2 min-h-[44px] touch-manipulation" onClick={() => { if (isInstallable) promptInstall(); else navigate('/install'); setMobileMenuOpen(false); }}>
                    <Download className="w-4 h-4" />Instalar Aplicação
                  </Button>
                )}
                <Button variant="outline" className="w-full mt-2" onClick={() => { navigate('/members/auth?tab=login'); setMobileMenuOpen(false); }}>Entrar</Button>
                <Button className="w-full mt-2" onClick={() => { navigate('/members/auth'); setMobileMenuOpen(false); }}>Criar Conta Gratuita</Button>
              </nav>
            </div>
          )}
        </header>

        <main className="flex-1 pt-16">
          {/* 1) HERO — Renders instantly, no framer-motion dependency */}
          <HeroSection onOpenApplicabilityModal={() => setShowApplicabilityModal(true)} />

          {/* 2) PARA QUEM É */}
          <LazySection minHeight="400px" rootMargin="300px">
            <Suspense fallback={null}>
              <TargetAudienceSection />
            </Suspense>
          </LazySection>

          {/* 3) ANTES / DEPOIS */}
          <LazySection minHeight="400px" rootMargin="300px">
            <Suspense fallback={null}>
              <BeforeAfterSection />
            </Suspense>
          </LazySection>

          {/* 4) O QUE GANHA QUEM CRIA CONTA */}
          <LazySection minHeight="400px" rootMargin="300px">
            <Suspense fallback={null}>
              <MemberValuePreview />
            </Suspense>
          </LazySection>

          {/* 5) CASO TÍPICO */}
          <LazySection minHeight="400px" rootMargin="300px">
            <Suspense fallback={null}>
              <TypicalCaseSection />
            </Suspense>
          </LazySection>

          {/* 6) O PROBLEMA (REFRAME CENTRAL) */}
          <LazySection minHeight="500px" rootMargin="300px">
            <Suspense fallback={null}>
              <ProblemSection />
            </Suspense>
          </LazySection>

          {/* 7) A SOLUÇÃO */}
          <LazySection minHeight="600px" rootMargin="200px">
            <Suspense fallback={null}>
              <SolutionSection />
            </Suspense>
          </LazySection>

          {/* 8) COMO FUNCIONA */}
          <LazySection minHeight="500px" rootMargin="200px">
            <Suspense fallback={null}>
              <HowItWorksSection />
            </Suspense>
          </LazySection>

          {/* SEO PILLAR LINKS */}
          <LazySection minHeight="400px" rootMargin="300px">
            <Suspense fallback={null}>
              <SEOPillarLinks />
            </Suspense>
          </LazySection>

          {/* GUIAS TÉCNICOS */}
          <LazySection minHeight="300px" rootMargin="300px">
            <Suspense fallback={null}>
              <TechnicalGuidesLinks />
            </Suspense>
          </LazySection>

          {/* ESTUDOS E RECURSOS TÉCNICOS */}
          <LazySection minHeight="300px" rootMargin="300px">
            <Suspense fallback={null}>
              <AuthorityAssetsSection />
            </Suspense>
          </LazySection>

          {/* ENGENHEIRO RESPONSÁVEL — static, no animation needed */}
          <LazySection minHeight="300px" rootMargin="300px">
            <section className="py-16 md:py-20 border-t border-border">
              <div className="container">
                <div className="max-w-3xl mx-auto">
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                      <User className="w-4 h-4" />
                      Engenheiro Responsável
                    </div>
                  </div>
                  <div className="p-6 md:p-8 rounded-2xl bg-card border border-border">
                    <div className="flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
                      <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                        <img src={founderPhoto} alt="André Araujo - Engenheiro Eletrotécnico" className="w-full h-full object-cover" loading="lazy" decoding="async" width="96" height="96" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-foreground mb-1">André Araujo</h3>
                        <p className="text-primary font-medium mb-3">Engenheiro Eletrotécnico | Fundador</p>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Especialização em elevadores e aplicação normativa EN 81. Governação técnica independente — sem execução, sem venda, sem representação de operadores.
                        </p>
                        <a href="https://www.linkedin.com/in/araujoandrelp" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-primary hover:underline mt-3">
                          Ver perfil LinkedIn
                          <ArrowRight className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </LazySection>

          {/* 9) FECHO FINAL */}
          <LazySection minHeight="300px" rootMargin="200px">
            <Suspense fallback={null}>
              <FinalCTASection />
            </Suspense>
          </LazySection>
        </main>

        <Footer variant="full" showWebsiteLink />
      </div>

      {/* Modal de Aplicabilidade */}
      <Dialog open={showApplicabilityModal} onOpenChange={setShowApplicabilityModal}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Este serviço aplica-se ao seu caso?</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              O ElevAdvisor é indicado para decisores que enfrentam situações técnicas onde precisam de uma opinião independente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground">Este serviço é para si se:</p>
              <ul className="space-y-2">
                {whenToUse.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="border-t border-border pt-4 space-y-3">
              <p className="text-sm font-medium text-foreground">O que oferecemos:</p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2"><Shield className="w-4 h-4 text-primary shrink-0 mt-0.5" /><span>Apoio técnico independente baseado na EN 81</span></li>
                <li className="flex items-start gap-2"><Scale className="w-4 h-4 text-primary shrink-0 mt-0.5" /><span>Análise sem conflitos de interesse comercial</span></li>
                <li className="flex items-start gap-2"><FileCheck className="w-4 h-4 text-primary shrink-0 mt-0.5" /><span>Parecer documentado e rastreável</span></li>
              </ul>
            </div>
            <div className="bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground">
              <strong>Nota:</strong> Não fazemos manutenção, não vendemos equipamentos, não representamos operadores. A nossa independência é estrutural.
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button variant="outline" onClick={() => { trackEvent('applicability_modal_close', { action: 'voltar', page_path: window.location.pathname }); setShowApplicabilityModal(false); }} className="flex-1">Voltar</Button>
            <Button onClick={() => { trackEvent('applicability_modal_conversion', { action: 'criar_conta', page_path: window.location.pathname }); trackHeroCTA('secondary', 'Criar conta gratuita (modal)'); setShowApplicabilityModal(false); navigate('/members/auth'); }} className="flex-1 gap-2">
              Criar conta gratuita
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
